/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import { Grid, Hidden, Typography2 } from '@coursera/cds-core';
import { UploadIcon } from '@coursera/cds-icons';

import { getSettingsLink } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import { Button } from 'bundles/enterprise-ui/components/Button';
import { truncate } from 'bundles/enterprise-ui/utils';
import CourseraLogo from 'bundles/page/components/CourseraLogo';

import _t from 'i18n!nls/enterprise-admin-home';

const TRUNCATE_LENGTH = 50;

const styles = {
  courseraLogo: css`
    height: 34px;
  `,
  companyLogo: css`
    max-height: 70px;
    max-width: 250px;
  `,
  boxMargin: css`
    margin-bottom: 24px;
  `,
  wrapper: css`
    margin: 48px 0;
  `,
};

type Props = {
  logo: string;
  canManageOrganization: boolean;
  thirdPartyOrganization: { name: string };
};

function LandingPageWelcome({ logo, canManageOrganization, thirdPartyOrganization }: Props) {
  const router = useRouter();
  const userData = user.get();
  const message = _t("Your team will see your organization's logo in invitation emails and in learning programs.");
  return (
    <Grid container direction="column" alignItems="center" css={styles.wrapper}>
      <Typography2 variant="titleMedium" component="h1" css={{ marginBottom: 'var(--cds-spacing-300)' }}>
        <FormattedMessage message={_t('Welcome, {fullName}.')} fullName={userData.fullName} />
      </Typography2>
      <Grid
        container
        wrap="wrap"
        alignItems="flex-start"
        justifyContent="center"
        css={canManageOrganization ? styles.boxMargin : undefined}
        spacing={16}
      >
        <Grid item>
          <CourseraLogo css={styles.courseraLogo} hexColorCode="var(--cds-color-blue-700)" />
        </Grid>
        <Grid item>
          <Typography2 component="div" variant="titleSmall">
            +
          </Typography2>
        </Grid>
        <Grid item>
          {logo && <img css={styles.companyLogo} src={logo} alt={_t('Your Company Logo')} />}
          {!logo && (
            <Typography2 component="div" variant="titleSmall">
              {truncate(thirdPartyOrganization.name, TRUNCATE_LENGTH)}
            </Typography2>
          )}
          {!logo && canManageOrganization && (
            <Hidden smDown>
              <Button
                onClick={() => router.push(getSettingsLink(router.params))}
                variant="secondary"
                size="small"
                icon={<UploadIcon />}
                css={{ margin: '16px 0 8px' }}
              >
                {_t('Upload logo')}
              </Button>
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {message}
              </Typography2>
            </Hidden>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

export default LandingPageWelcome;
